import { create } from "zustand";
import { combine } from "zustand/middleware";
import type { DetectedMeshFeature, DetectedMeshFeatureMap } from "../types/analysis";
import { generateFeatureColorPalette } from "../utils/featureColors";
import { getProjectSaveActions } from "./projectSaveStore";
import {
  DEFAULT_JOINT_VISIBILITY_UI_STATE,
  type JointVisibilityUiState,
} from "../selected-joint-visibility/visibilityEvents";
import {
  type CollisionGeomKindKey,
  DEFAULT_COLLISION_GEOM_COLORS_BY_KIND,
} from "../utils/collisionGeomKinds";

/**
 * solid — one color; rainbow — per-geom palette; byKind — color by MJCF geom kind (sphere, capsule, …).
 */
export type CollisionGeomColorMode = "solid" | "rainbow" | "byKind";

type ViewerState = {
  // Visualization toggles
  showCollision: boolean;
  /** Hex color (e.g. `#ff2222`) for collision geometry visualization materials. */
  collisionGeomColor: string;
  collisionGeomColorMode: CollisionGeomColorMode;
  /** Legend colors when {@link collisionGeomColorMode} is `byKind`. */
  collisionGeomColorsByKind: Record<CollisionGeomKindKey, string>;
  /** Hex color for site marker meshes when the site is not selected. */
  siteVizColor: string;
  /** Hex color for the currently selected site’s marker mesh. */
  siteVizSelectedColor: string;
  showSites: boolean;
  showVisual: boolean;
  showJoints: boolean;
  showInertials: boolean;
  showGrasps: boolean;
  showPositionGizmo: boolean;
  showConstraintMarkers: boolean;
  showActuators: boolean;

  // Per-joint visualization visibility map (jointName -> visible)
  jointVizVisibility: Record<string, boolean>;

  // Per-item visibility overrides (true = visible, false = individually hidden).
  // When an item is false here it stays hidden even if the global layer flag is on.
  // When the global flag is off, everything in that layer is hidden regardless.
  bodyVisibility: Record<string, boolean>;
  siteVisibility: Record<string, boolean>;
  /** Keyed by `bodyUuid:geomUuid` – same format as selectionStore collision keys. */
  collisionGeomVisibility: Record<string, boolean>;
  /** Keyed by inertial selection key (`bodyUuid:id`). */
  inertialVisibility: Record<string, boolean>;
  /** Keyed by equality constraint uuid — hides that constraint's markers only (constraint overlay layer must be on). */
  equalityMarkerVisibility: Record<string, boolean>;
  /** Keyed by tendon uuid — hides that tendon's overlay markers only (constraint overlay layer must be on). */
  tendonMarkerVisibility: Record<string, boolean>;
  /** Keyed by saved scene camera uuid — hides that camera's frustum/sphere marker only (camera markers layer must be on). */
  cameraMarkerVisibility: Record<string, boolean>;

  // Per-mesh viewer state
  meshVisibility: Record<string, boolean>;
  meshOpacity: Record<string, number>;
  meshFlatShading: Record<string, boolean>;

  /** Joint inspector toolbar options that persist while clicking between joints. */
  jointInspectorVisibility: JointVisibilityUiState;

  // Visualization helpers
  inertiaMassRange: [number | null, number | null];

  // UI toggles
  showPerformancePanel: boolean;

  // Coordinate system preference
  useYUp: boolean;

  /** When enabled for a kind, first left-click on scene tries that inspect target (see Scene.jsx). */
  scenePrioritySelection: Record<ScenePrioritySelectionKey, boolean>;

  // Transform / gizmo state
  transformMode: "translate" | "rotate" | "scale";
  isGizmoActive: boolean;
  detectedMeshFeatures: DetectedMeshFeatureMap;
};

type FeatureInput = Omit<DetectedMeshFeature, "id" | "color" | "createdAt">;

/** Keys align with property panel kinds; left-click priority uses the same flags. */
export type ScenePrioritySelectionKey =
  | "body"
  | "object"
  | "joint"
  | "mesh"
  | "visualGeom"
  | "collision"
  | "site"
  | "inertial"
  | "constraint"
  | "tendon";

export const DEFAULT_SCENE_PRIORITY_SELECTION: Record<ScenePrioritySelectionKey, boolean> = {
  body: true,
  object: false,
  joint: false,
  mesh: false,
  visualGeom: false,
  collision: false,
  site: false,
  inertial: false,
  constraint: false,
  tendon: false,
};

type ViewerActions = {
  toggleShowCollision: () => void;
  setCollisionGeomColor: (_hex: string) => void;
  setCollisionGeomColorMode: (_mode: CollisionGeomColorMode) => void;
  setCollisionGeomColorByKind: (_kind: CollisionGeomKindKey, _hex: string) => void;
  setSiteVizColor: (_hex: string) => void;
  setSiteVizSelectedColor: (_hex: string) => void;
  toggleShowSites: () => void;
  toggleShowVisual: () => void;
  toggleShowJoints: () => void;
  toggleShowInertials: () => void;
  toggleShowGrasps: () => void;
  toggleShowPositionGizmo: () => void;
  toggleShowConstraintMarkers: () => void;
  toggleShowActuators: () => void;
  toggleShowPerformancePanel: () => void;
  setUseYUp: (useYUp: boolean) => void;
  setScenePrioritySelection: (
    key: ScenePrioritySelectionKey | ScenePrioritySelectionKey[],
    enabled: boolean
  ) => void;
  // Per-joint visualization visibility controls
  setJointVizVisible: (jointName: string, visible: boolean) => void;
  setJointVizVisibilityMap: (map: Record<string, boolean>) => void;
  // Per-item visibility overrides
  setBodyVisibility: (bodyUuid: string, visible: boolean) => void;
  setSiteVisibility: (siteUuid: string, visible: boolean) => void;
  setCollisionGeomVisibility: (key: string, visible: boolean) => void;
  setInertialVisibility: (key: string, visible: boolean) => void;
  setEqualityMarkerVisibility: (equalityUuid: string, visible: boolean) => void;
  setTendonMarkerVisibility: (tendonUuid: string, visible: boolean) => void;
  setCameraMarkerVisibility: (cameraUuid: string, visible: boolean) => void;
  setMeshVisibility: (meshName: string, visible: boolean) => void;
  setMeshOpacity: (meshName: string, opacity: number) => void;
  setMeshFlatShading: (meshName: string, isFlat: boolean) => void;
  setJointInspectorVisibility: (patch: Partial<JointVisibilityUiState>) => void;
  setInertiaMassRange: (min: number | null, max: number | null) => void;
  setTransformMode: (mode: "translate" | "rotate" | "scale") => void;
  setIsGizmoActive: (isActive: boolean) => void;
  setDetectedMeshFeatures: (meshName: string, features: DetectedMeshFeature[]) => void;
  addDetectedMeshFeature: (meshName: string, feature: FeatureInput) => void;
  addDetectedMeshFeatures: (meshName: string, features: FeatureInput[]) => void;
  removeDetectedMeshFeature: (meshName: string, featureId: string) => void;
  clearDetectedMeshFeatures: (meshName?: string) => void;
  getDetectedMeshFeatures: (meshName: string) => DetectedMeshFeature[];
  resetViewer: () => void;
};

export type ViewerStore = ViewerState & { actions: ViewerActions };

/** Accept `#rgb` / `#rrggbb`; returns normalized `#rrggbb` or null. */
export function normalizeCollisionGeomHexColor(input: string): string | null {
  const s = String(input).trim();
  const m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return `#${h}`;
}

const createInitialState = (): ViewerState => ({
  // Visualization toggles
  showCollision: false,
  collisionGeomColor: "#ff2222",
  collisionGeomColorMode: "rainbow",
  collisionGeomColorsByKind: { ...DEFAULT_COLLISION_GEOM_COLORS_BY_KIND },
  siteVizColor: "#ffff00",
  siteVizSelectedColor: "#ff00ff",
  showSites: false,
  showVisual: true,
  showJoints: false,
  showInertials: false,
  showGrasps: false,
  showPositionGizmo: false,
  showConstraintMarkers: false,
  showActuators: false,

  // Per-joint visualization visibility map
  jointVizVisibility: {},

  // Per-item visibility overrides
  bodyVisibility: {},
  siteVisibility: {},
  collisionGeomVisibility: {},
  inertialVisibility: {},
  equalityMarkerVisibility: {},
  tendonMarkerVisibility: {},
  cameraMarkerVisibility: {},

  // Per-mesh viewer state
  meshVisibility: {},
  meshOpacity: {},
  meshFlatShading: {},

  jointInspectorVisibility: { ...DEFAULT_JOINT_VISIBILITY_UI_STATE },

  // Visualization helpers
  inertiaMassRange: [null, null],

  // UI toggles
  showPerformancePanel: false,

  // Coordinate system preference
  useYUp: false,

  scenePrioritySelection: { ...DEFAULT_SCENE_PRIORITY_SELECTION },

  // Transform / gizmo state
  transformMode: "translate",
  isGizmoActive: false,
  detectedMeshFeatures: {},
});

const EMPTY_FEATURES: DetectedMeshFeature[] = [];

const cloneFeature = (feature: DetectedMeshFeature): DetectedMeshFeature => ({
  ...feature,
  center: Array.isArray(feature.center)
    ? ([...feature.center] as [number, number, number])
    : feature.center,
  normal: Array.isArray(feature.normal)
    ? ([...feature.normal] as [number, number, number])
    : feature.normal,
});

export const useViewerStore = create<ViewerStore>()(
  combine(
    createInitialState(),

    (set, get) => ({
      actions: {
        toggleShowCollision: () => set((state) => ({ showCollision: !state.showCollision })),
        setCollisionGeomColor: (hex: string) => {
          const normalized = normalizeCollisionGeomHexColor(hex);
          if (!normalized) return;
          set({ collisionGeomColor: normalized });
        },
        setCollisionGeomColorMode: (mode: CollisionGeomColorMode) =>
          set({ collisionGeomColorMode: mode }),
        setCollisionGeomColorByKind: (kind: CollisionGeomKindKey, hex: string) => {
          const normalized = normalizeCollisionGeomHexColor(hex);
          if (!normalized) return;
          set((state) => ({
            collisionGeomColorsByKind: {
              ...state.collisionGeomColorsByKind,
              [kind]: normalized,
            },
          }));
        },
        setSiteVizColor: (hex: string) => {
          const normalized = normalizeCollisionGeomHexColor(hex);
          if (!normalized) return;
          set({ siteVizColor: normalized });
        },
        setSiteVizSelectedColor: (hex: string) => {
          const normalized = normalizeCollisionGeomHexColor(hex);
          if (!normalized) return;
          set({ siteVizSelectedColor: normalized });
        },
        toggleShowSites: () => set((state) => ({ showSites: !state.showSites })),
        toggleShowVisual: () => set((state) => ({ showVisual: !state.showVisual })),
        toggleShowJoints: () => set((state) => ({ showJoints: !state.showJoints })),
        toggleShowInertials: () => set((state) => ({ showInertials: !state.showInertials })),
        toggleShowGrasps: () => set((state) => ({ showGrasps: !state.showGrasps })),
        toggleShowConstraintMarkers: () =>
          set((state) => ({ showConstraintMarkers: !state.showConstraintMarkers })),
        toggleShowActuators: () => set((state) => ({ showActuators: !state.showActuators })),
        toggleShowPositionGizmo: () =>
          set((state) => ({ showPositionGizmo: !state.showPositionGizmo })),
        toggleShowPerformancePanel: () =>
          set((state) => ({ showPerformancePanel: !state.showPerformancePanel })),

        setUseYUp: (useYUp: boolean) => set({ useYUp }),

        setScenePrioritySelection: (key, enabled) =>
          set(() => {
            const selectedKeys = new Set(
              (Array.isArray(key) ? key : [key]) as ScenePrioritySelectionKey[]
            );
            return {
              scenePrioritySelection: (
                Object.keys(DEFAULT_SCENE_PRIORITY_SELECTION) as ScenePrioritySelectionKey[]
              ).reduce(
                (acc, candidateKey) => {
                  acc[candidateKey] = !!enabled && selectedKeys.has(candidateKey);
                  return acc;
                },
                {} as Record<ScenePrioritySelectionKey, boolean>
              ),
            };
          }),

        // Per-joint visualization visibility controls
        setJointVizVisible: (jointName: string, visible: boolean) =>
          set((state) => ({
            jointVizVisibility: { ...state.jointVizVisibility, [jointName]: !!visible },
          })),

        setJointVizVisibilityMap: (map: Record<string, boolean>) =>
          set(() => ({ jointVizVisibility: { ...map } })),

        setBodyVisibility: (bodyUuid: string, visible: boolean) => {
          set((state) => ({
            bodyVisibility: { ...state.bodyVisibility, [bodyUuid]: !!visible },
          }));
          getProjectSaveActions().markProjectDirty();
        },

        setSiteVisibility: (siteUuid: string, visible: boolean) =>
          set((state) => ({
            siteVisibility: { ...state.siteVisibility, [siteUuid]: !!visible },
          })),

        setCollisionGeomVisibility: (key: string, visible: boolean) => {
          set((state) => ({
            collisionGeomVisibility: { ...state.collisionGeomVisibility, [key]: !!visible },
          }));
          getProjectSaveActions().markProjectDirty();
        },

        setInertialVisibility: (key: string, visible: boolean) =>
          set((state) => ({
            inertialVisibility: { ...state.inertialVisibility, [key]: !!visible },
          })),

        setEqualityMarkerVisibility: (equalityUuid: string, visible: boolean) =>
          set((state) => ({
            equalityMarkerVisibility: {
              ...state.equalityMarkerVisibility,
              [equalityUuid]: !!visible,
            },
          })),

        setTendonMarkerVisibility: (tendonUuid: string, visible: boolean) =>
          set((state) => ({
            tendonMarkerVisibility: {
              ...state.tendonMarkerVisibility,
              [tendonUuid]: !!visible,
            },
          })),

        setCameraMarkerVisibility: (cameraUuid: string, visible: boolean) =>
          set((state) => ({
            cameraMarkerVisibility: {
              ...state.cameraMarkerVisibility,
              [cameraUuid]: !!visible,
            },
          })),

        setMeshVisibility: (meshName: string, visible: boolean) => {
          set((state) => ({
            meshVisibility: { ...state.meshVisibility, [meshName]: visible },
          }));
          getProjectSaveActions().markProjectDirty();
        },

        setMeshOpacity: (meshName: string, opacity: number) =>
          set((state) => ({
            meshOpacity: { ...state.meshOpacity, [meshName]: opacity },
          })),

        setMeshFlatShading: (meshName: string, isFlat: boolean) =>
          set((state) => ({
            meshFlatShading: { ...state.meshFlatShading, [meshName]: isFlat },
          })),

        setJointInspectorVisibility: (patch: Partial<JointVisibilityUiState>) =>
          set((state) => ({
            jointInspectorVisibility: { ...state.jointInspectorVisibility, ...patch },
          })),

        setInertiaMassRange: (min: number | null, max: number | null) =>
          set({ inertiaMassRange: [min, max] }),

        setTransformMode: (mode) => set({ transformMode: mode }),
        setIsGizmoActive: (isActive: boolean) => set({ isGizmoActive: isActive }),

        setDetectedMeshFeatures: (meshName, features) =>
          set((state) => ({
            detectedMeshFeatures: {
              ...state.detectedMeshFeatures,
              [meshName]: Array.isArray(features) ? features.map(cloneFeature) : [],
            },
          })),

        addDetectedMeshFeature: (meshName, feature) =>
          set((state) => {
            const totalExisting = Object.values(state.detectedMeshFeatures || {}).reduce(
              (acc, arr) => acc + arr.length,
              0
            );
            const colors = generateFeatureColorPalette(totalExisting + 1);
            const color = colors[totalExisting] || 0x00ff88;
            const newFeature: DetectedMeshFeature = {
              ...feature,
              id: `${feature.method}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              color,
              createdAt: Date.now(),
              center: Array.isArray(feature.center)
                ? ([...feature.center] as [number, number, number])
                : feature.center,
              normal: Array.isArray(feature.normal)
                ? ([...feature.normal] as [number, number, number])
                : feature.normal,
            };
            const current = state.detectedMeshFeatures[meshName] || [];
            return {
              detectedMeshFeatures: {
                ...state.detectedMeshFeatures,
                [meshName]: [...current, newFeature],
              },
            };
          }),

        addDetectedMeshFeatures: (meshName, features) =>
          set((state) => {
            const current = state.detectedMeshFeatures[meshName] || [];
            const totalExisting = Object.values(state.detectedMeshFeatures || {}).reduce(
              (acc, arr) => acc + arr.length,
              0
            );
            const colors = generateFeatureColorPalette(totalExisting + features.length);
            const baseIndex = totalExisting;
            const createdAtBase = Date.now();
            const newFeatures = features.map((feature, index) => ({
              ...feature,
              id: `${feature.method}_${createdAtBase}_${index}_${Math.random()
                .toString(36)
                .slice(2, 11)}`,
              color: colors[baseIndex + index] || 0x00ff88,
              createdAt: createdAtBase + index,
              center: Array.isArray(feature.center)
                ? ([...feature.center] as [number, number, number])
                : feature.center,
              normal: Array.isArray(feature.normal)
                ? ([...feature.normal] as [number, number, number])
                : feature.normal,
            }));
            return {
              detectedMeshFeatures: {
                ...state.detectedMeshFeatures,
                [meshName]: [...current, ...newFeatures],
              },
            };
          }),

        removeDetectedMeshFeature: (meshName, featureId) =>
          set((state) => {
            const current = state.detectedMeshFeatures[meshName] || [];
            return {
              detectedMeshFeatures: {
                ...state.detectedMeshFeatures,
                [meshName]: current.filter((feature) => feature.id !== featureId),
              },
            };
          }),

        clearDetectedMeshFeatures: (meshName) =>
          set((state) => {
            if (meshName) {
              const next = { ...state.detectedMeshFeatures };
              delete next[meshName];
              return { detectedMeshFeatures: next };
            }
            return { detectedMeshFeatures: {} };
          }),

        getDetectedMeshFeatures: (meshName) => {
          const map = get().detectedMeshFeatures || {};
          const features = map[meshName] || EMPTY_FEATURES;
          return features.map(cloneFeature);
        },

        resetViewer: () =>
          set({
            ...createInitialState(),
          }),
      },
    })
  )
);

export const useMeshVisibility = () => useViewerStore((state) => state.meshVisibility);
export const useMeshOpacity = () => useViewerStore((state) => state.meshOpacity);
export const useMeshFlatShading = () => useViewerStore((state) => state.meshFlatShading);
export const useInertiaMassRange = () => useViewerStore((state) => state.inertiaMassRange);

/** True when the global visual layer is ON but ≥1 body or mesh is individually hidden. */
export const useHasSomeHiddenVisuals = () =>
  useViewerStore(
    (s) =>
      s.showVisual &&
      (Object.values(s.bodyVisibility).some((v) => v === false) ||
        Object.values(s.meshVisibility).some((v) => v === false))
  );

/** True when the global joints layer is ON but ≥1 joint is individually hidden. */
export const useHasSomeHiddenJoints = () =>
  useViewerStore(
    (s) => s.showJoints && Object.values(s.jointVizVisibility).some((v) => v === false)
  );

/** True when the global sites layer is ON but ≥1 site is individually hidden. */
export const useHasSomeHiddenSites = () =>
  useViewerStore((s) => s.showSites && Object.values(s.siteVisibility).some((v) => v === false));

/** True when the global inertials layer is ON but ≥1 inertial is individually hidden. */
export const useHasSomeHiddenInertials = () =>
  useViewerStore(
    (s) => s.showInertials && Object.values(s.inertialVisibility).some((v) => v === false)
  );

/** True when the global collision layer is ON but ≥1 collision geom is individually hidden. */
export const useHasSomeHiddenCollision = () =>
  useViewerStore(
    (s) => s.showCollision && Object.values(s.collisionGeomVisibility).some((v) => v === false)
  );
export const useDetectedMeshFeatures = (meshName: string) =>
  useViewerStore((state) => state.detectedMeshFeatures[meshName] ?? EMPTY_FEATURES);

export const useViewerActions = () => useViewerStore((state) => state.actions);

export const getViewerState = () => {
  const { actions, ...state } = useViewerStore.getState();
  return state;
};
export const getViewerActions = () => useViewerStore.getState().actions;
